import { useId, useState, type ReactNode } from "react";
import { Box, Dialog, DialogContent, IconButton, Typography } from "@mui/material";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { Button, Icon, ModalHeader } from "src/domains/shared/components";
import {
  COLOR_CONSTANTS,
  SPACING_CONSTANTS,
  TYPOGRAPHY_SCALE,
} from "src/domains/shared/constants/styles";
import { testIds } from "src/test/testIds";
import { GCP_MARKER_COPY_VARIABLES, GCP_MARKER_SIDE_MM } from "./gcpMarkerRule";
import { GUIDE_ALTITUDE_ROWS } from "./gcpGuideAltitudes";

const DIALOG_NAME = "gcp-auto-detect-guide";

/**
 * 70 cm 판 인쇄 파일 — `public/downloads/gcp-marker/` 의 정적 자산이고, 백엔드 표식 생성기의 정본과 바이트가 같아야 한다
 * (mapping-request 「표식 인쇄 파일 제공」). 크기를 판 한 변 상수에서 만들어 문구의 인쇄 크기와 내려받는 파일이 갈라지지 않는다.
 * SVG 는 판 둘레에 10 mm 여백을 둔 720 mm 쪽이라 미리보기로도 쓴다. 예전 1.0 m 판 파일은 이미 나간 링크를 위해 같은 폴더에
 * 남기되 링크하지 않는다(「예전 1.0 m 인쇄 파일의 보존」).
 */
const GCP_MARKER_FILE_STEM = `/downloads/gcp-marker/dronesquare-v1-${GCP_MARKER_SIDE_MM}mm`;
const GCP_MARKER_FILES = {
  pdf: `${GCP_MARKER_FILE_STEM}.pdf`,
  svg: `${GCP_MARKER_FILE_STEM}.svg`,
  a1Pdf: `${GCP_MARKER_FILE_STEM}-A1.pdf`,
} as const;

/** 가이드 안 표식 미리보기의 한 변 — 판의 두 검은 칸이 맞닿는 중심이 알아볼 수 있는 크기. */
const MARKER_PREVIEW_PX = 120;

/** 가이드의 항목 목록 키 — 리소스에서 문자열 배열이다. */
type GuideListKey =
  | "timeSeries.gcpGuide.what.items"
  | "timeSeries.gcpGuide.prepare.items"
  | "timeSeries.gcpGuide.install.items"
  | "timeSeries.gcpGuide.flight.itemsBeforeTable"
  | "timeSeries.gcpGuide.flight.itemsAfterTable"
  | "timeSeries.gcpGuide.upload.items"
  | "timeSeries.gcpGuide.fallback.items";

/**
 * GCP 자동 인식 가이드를 여는 버튼 — 열림 상태를 버튼이 가지므로 놓는 쪽은 버튼만 두면 된다.
 *
 * - `icon`(기본): "?" 도움말 버튼 — GCP 행 첫 줄에서 체크박스 바로 뒤에 선다(mapping-request 「자동 인식 가이드
 *   진입」). 이름은 `aria-label` 이다.
 * - `text`: 보이는 글이 곧 이름인 글자 버튼 — 안내 흐름 안에서 무엇을 여는지 글로 보인다.
 */
export function GcpAutoDetectGuideButton({
  variant = "icon",
}: {
  variant?: "icon" | "text";
}) {
  const { t } = useTranslation("projects");
  const [isOpen, setIsOpen] = useState(false);
  const label = t("timeSeries.gcpGuide.open");

  return (
    <>
      {variant === "icon" ? (
        <IconButton
          size="small"
          aria-label={label}
          aria-haspopup="dialog"
          onClick={() => setIsOpen(true)}
          sx={helpButton}
        >
          <Icon name="etc/QuestionCircle" size={16} />
        </IconButton>
      ) : (
        <Button
          size="small"
          intent="primary"
          emphasis="text"
          aria-haspopup="dialog"
          onClick={() => setIsOpen(true)}
        >
          {label}
        </Button>
      )}
      <GcpAutoDetectGuideDialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
}

/**
 * 자동 구축의 다섯 조건 목록 — 가이드와 매핑 요청 모달이 같은 리소스 키 하나로 그린다(mapping-request 「자동 인식 가이드 내용」·
 * 「자동 구축 안내」). 목록의 이름은 놓는 쪽의 제목·안내 문장이 `labelledBy` 로 준다.
 */
export function GcpAutoDetectConditionList({
  labelledBy,
}: {
  labelledBy: string;
}) {
  const { t } = useTranslation("projects");
  const items = t("timeSeries.gcpGuide.checklist.items", {
    ...GCP_MARKER_COPY_VARIABLES,
    returnObjects: true,
  }) as unknown as string[];

  return <GuideList items={items} labelledBy={labelledBy} />;
}

/**
 * GCP 자동 인식 가이드 — 읽기 전용 대화상자(mapping-request 「자동 인식 가이드 내용」). 본문만 스크롤되고, 닫으면 MUI Dialog 가
 * 초점을 연 버튼으로 돌려준다. 항목 목록은 리소스 배열이라 항목이 늘거나 줄어도 리소스만 바뀐다. 판 크기·GSD 숫자(`{{gcp…}}`)가
 * 든 문구는 `GCP_MARKER_COPY_VARIABLES` 를 넘겨 그린다.
 */
export function GcpAutoDetectGuideDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation("projects");
  const titleId = useId();
  const checklistTitleId = useId();
  const listOf = (key: GuideListKey) =>
    t(key, {
      ...GCP_MARKER_COPY_VARIABLES,
      returnObjects: true,
    }) as unknown as string[];
  /** 렌즈 행의 기체 칸 글(`flight.tableRows.*`) — 한 행에 기체가 여럿이면 그 이름들을 이어 붙인 글이라 기체 하나의 이름(`gcpGuide.aircraft.*`)이 아니다. */
  const aircraftRowLabelOf = (
    altitudeRow: (typeof GUIDE_ALTITUDE_ROWS)[number]
  ) => t(`timeSeries.gcpGuide.flight.tableRows.${altitudeRow.id}`);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      aria-labelledby={titleId}
      data-testid={testIds.modal.root(DIALOG_NAME)}
      slotProps={{ paper: { sx: paper } }}
    >
      <ModalHeader
        title={t("timeSeries.gcpGuide.title")}
        titleComponent="h2"
        titleId={titleId}
        onClose={onClose}
        closeButtonProps={{
          "data-testid": testIds.modal.closeBtn(DIALOG_NAME),
        }}
      />
      <DialogContent dividers sx={content}>
        <GuideSection title={t("timeSeries.gcpGuide.what.title")}>
          <GuideList items={listOf("timeSeries.gcpGuide.what.items")} />
          <Typography component="h4" id={checklistTitleId} sx={checklistTitle}>
            {t("timeSeries.gcpGuide.checklist.title")}
          </Typography>
          <GcpAutoDetectConditionList labelledBy={checklistTitleId} />
        </GuideSection>

        <GuideSection title={t("timeSeries.gcpGuide.prepare.title")}>
          <Box sx={markerFiles}>
            <Image
              src={GCP_MARKER_FILES.svg}
              alt={t("timeSeries.gcpGuide.prepare.previewAlt")}
              width={MARKER_PREVIEW_PX}
              height={MARKER_PREVIEW_PX}
              unoptimized
              style={markerPreview}
            />
            <Box sx={markerDownloads}>
              <Typography sx={bodyText}>
                {t(
                  "timeSeries.gcpGuide.prepare.printFile",
                  GCP_MARKER_COPY_VARIABLES
                )}
              </Typography>
              <Box sx={downloadButtons}>
                <MarkerDownloadLink
                  href={GCP_MARKER_FILES.pdf}
                  label={t("timeSeries.gcpGuide.prepare.downloadPdf")}
                />
                <MarkerDownloadLink
                  href={GCP_MARKER_FILES.svg}
                  label={t("timeSeries.gcpGuide.prepare.downloadSvg")}
                />
              </Box>
              <Box sx={downloadButtons}>
                <MarkerDownloadLink
                  href={GCP_MARKER_FILES.a1Pdf}
                  label={t("timeSeries.gcpGuide.prepare.downloadA1")}
                  emphasis="text"
                />
                <Typography sx={noteText}>
                  {t("timeSeries.gcpGuide.prepare.a1Note")}
                </Typography>
              </Box>
            </Box>
          </Box>
          <GuideList items={listOf("timeSeries.gcpGuide.prepare.items")} />
        </GuideSection>

        <GuideSection title={t("timeSeries.gcpGuide.install.title")}>
          <GuideList items={listOf("timeSeries.gcpGuide.install.items")} />
        </GuideSection>

        <GuideSection title={t("timeSeries.gcpGuide.flight.title")}>
          <GuideList
            items={listOf("timeSeries.gcpGuide.flight.itemsBeforeTable")}
          />
          <Box component="table" sx={altitudeTable}>
            <Box component="caption" sx={tableCaption}>
              {t(
                "timeSeries.gcpGuide.flight.tableCaption",
                GCP_MARKER_COPY_VARIABLES
              )}
            </Box>
            <thead>
              <tr>
                <Box component="th" scope="col" sx={tableHeadCell}>
                  {t("timeSeries.gcpGuide.flight.tableHeaders.aircraft")}
                </Box>
                <Box component="th" scope="col" sx={tableHeadCell}>
                  {t("timeSeries.gcpGuide.flight.tableHeaders.altitude")}
                </Box>
              </tr>
            </thead>
            <tbody>
              {GUIDE_ALTITUDE_ROWS.map((altitudeRow) => (
                <tr key={altitudeRow.id}>
                  <Box component="td" sx={tableCell}>
                    {aircraftRowLabelOf(altitudeRow)}
                  </Box>
                  <Box component="td" sx={tableCell}>
                    {t("timeSeries.gcpGuide.flight.tableAltitude", {
                      meters: altitudeRow.altitudeM,
                    })}
                  </Box>
                </tr>
              ))}
            </tbody>
          </Box>
          {GUIDE_ALTITUDE_ROWS.filter(
            (altitudeRow) => altitudeRow.isCappedByValidatedRange
          ).map((altitudeRow) => (
            <Typography key={altitudeRow.id} sx={noteText}>
              {t("timeSeries.gcpGuide.flight.validatedRangeNote", {
                aircraft: aircraftRowLabelOf(altitudeRow),
                meters: altitudeRow.altitudeM,
              })}
            </Typography>
          ))}
          <GuideList
            items={listOf("timeSeries.gcpGuide.flight.itemsAfterTable")}
          />
        </GuideSection>

        <GuideSection title={t("timeSeries.gcpGuide.upload.title")}>
          <GuideList items={listOf("timeSeries.gcpGuide.upload.items")} />
        </GuideSection>

        <GuideSection title={t("timeSeries.gcpGuide.fallback.title")}>
          <GuideList items={listOf("timeSeries.gcpGuide.fallback.items")} />
        </GuideSection>
      </DialogContent>
    </Dialog>
  );
}

/** 가이드의 한 절 — 대화상자 제목(h2) 아래 h3 로 선다. */
function GuideSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Box component="section" sx={section}>
      <Typography component="h3" sx={sectionTitle}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function GuideList({
  items,
  labelledBy,
}: {
  items: string[];
  /** 목록에 이름을 주는 제목·안내 문장의 id. */
  labelledBy?: string;
}) {
  return (
    <Box component="ul" aria-labelledby={labelledBy} sx={list}>
      {items.map((item) => (
        <Typography component="li" key={item} sx={bodyText}>
          {item}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * 표식 인쇄 파일 내려받기 링크 — 같은 출처의 정적 파일이라 `download` 로 원래 파일명 그대로 받는다(mapping-request
 * 「표식 인쇄 파일 제공」). 공유 `Button` 은 `href` 가 있으면 `<a>` 로 그리지만 props 타입이 `<button>` 기준이라
 * `download` 는 펼침으로 넘긴다.
 */
function MarkerDownloadLink({
  href,
  label,
  emphasis = "outlined",
}: {
  href: string;
  label: string;
  emphasis?: "outlined" | "text";
}) {
  const anchorAttributes = { download: href.split("/").pop() };
  return (
    <Button
      size="small"
      intent="primary"
      emphasis={emphasis}
      href={href}
      {...anchorAttributes}
    >
      {label}
    </Button>
  );
}

/** 도움말 버튼 — 체크박스 줄의 다른 컨트롤과 높이를 맞추도록 안쪽 여백만 남긴다. */
const helpButton = {
  padding: `${SPACING_CONSTANTS.spacing[1]}px`,
  flexShrink: 0,
} as const;

/** 가이드는 긴 읽을거리라 확인창보다 넓다. 좁은 화면에서는 좌우 여백을 남기고 줄어든다. */
const GUIDE_DIALOG_WIDTH = "640px";

const paper = {
  width: GUIDE_DIALOG_WIDTH,
  maxWidth: `calc(100vw - ${SPACING_CONSTANTS.spacing[8]}px)`,
  borderRadius: SPACING_CONSTANTS.borderRadius.lg,
} as const;

const content = {
  display: "flex",
  flexDirection: "column",
  gap: `${SPACING_CONSTANTS.spacing[5]}px`,
  padding: `${SPACING_CONSTANTS.spacing[4]}px ${SPACING_CONSTANTS.spacing[6]}px`,
} as const;

const section = {
  display: "flex",
  flexDirection: "column",
  gap: `${SPACING_CONSTANTS.spacing[2]}px`,
} as const;

const sectionTitle = {
  ...TYPOGRAPHY_SCALE.subtitle1,
  fontWeight: 700,
  color: COLOR_CONSTANTS.text.primary,
  margin: 0,
} as const;

/** 다섯 조건 목록의 제목 — 절 제목(h3)보다 한 단계 낮은 강조다. */
const checklistTitle = {
  ...TYPOGRAPHY_SCALE.body2,
  fontWeight: 700,
  color: COLOR_CONSTANTS.text.primary,
  margin: 0,
} as const;

const list = {
  margin: 0,
  paddingLeft: `${SPACING_CONSTANTS.spacing[5]}px`,
  display: "flex",
  flexDirection: "column",
  gap: `${SPACING_CONSTANTS.spacing[1]}px`,
} as const;

const bodyText = {
  ...TYPOGRAPHY_SCALE.body2,
  color: COLOR_CONSTANTS.text.primary,
  whiteSpace: "normal",
} as const;

const noteText = {
  ...TYPOGRAPHY_SCALE.body2,
  color: COLOR_CONSTANTS.text.secondary,
} as const;

const markerFiles = {
  display: "flex",
  alignItems: "center",
  gap: `${SPACING_CONSTANTS.spacing[4]}px`,
} as const;

/** 흰 판이 흰 대화상자 위에서도 보이도록 테두리를 둔다. */
const markerPreview = {
  flexShrink: 0,
  border: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
  borderRadius: SPACING_CONSTANTS.borderRadius.sm,
} as const;

const markerDownloads = {
  display: "flex",
  flexDirection: "column",
  gap: `${SPACING_CONSTANTS.spacing[2]}px`,
  minWidth: 0,
} as const;

const downloadButtons = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: `${SPACING_CONSTANTS.spacing[2]}px`,
} as const;

const altitudeTable = {
  borderCollapse: "collapse",
  width: "100%",
  ...TYPOGRAPHY_SCALE.body2,
} as const;

const tableCaption = {
  ...TYPOGRAPHY_SCALE.body2,
  captionSide: "top",
  textAlign: "left",
  color: COLOR_CONSTANTS.text.secondary,
  paddingBottom: `${SPACING_CONSTANTS.spacing[1]}px`,
} as const;

const tableHeadCell = {
  textAlign: "left",
  fontWeight: 700,
  padding: `${SPACING_CONSTANTS.spacing[1]}px ${SPACING_CONSTANTS.spacing[2]}px`,
  borderBottom: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
} as const;

const tableCell = {
  padding: `${SPACING_CONSTANTS.spacing[1]}px ${SPACING_CONSTANTS.spacing[2]}px`,
  borderBottom: `1px solid ${COLOR_CONSTANTS.divider.divider}`,
} as const;
